import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import {
  DataLayerObject,
  GTMWindow,
  PageViewEvent,
  GenericEvent,
  PromoImpressionsEvent,
  PromoClickEvent,
  ProductImpressionsEvent,
  ProductClickEvent,
  ProductDetailEvent,
  AddToCartEvent,
  RemoveFromCartEvent,
  CheckoutEvent,
  CheckoutOptionEvent,
  PurchaseEvent,
} from './gtm-interfaces';

/**
 * Google Tag Manager Service
 * Provides a typed, safe interface for interacting with GTM's dataLayer
 */
@Injectable({
  providedIn: 'root',
})
export class GTMService {
  constructor(@Inject(PLATFORM_ID) private platformId: Object) {}

  private get window(): GTMWindow | null {
    return isPlatformBrowser(this.platformId) ? (window as GTMWindow) : null;
  }

  private get dataLayer(): DataLayerObject[] {
    if (!this.window) {
      return [];
    }

    if (!this.window.dataLayer) {
      this.window.dataLayer = [];
    }

    return this.window.dataLayer;
  }

  /**
   * Check if a string looks like a token/hash based on its characteristics
   *
   * @param segment URL segment to analyze
   * @returns true if segment appears to be a token
   */
  private isLikelyToken(segment: string): boolean {
    // Must be at least 20 characters (tokens are usually longer)
    if (segment.length < 20) {
      return false;
    }

    // Must be alphanumeric (with optional - or _)
    if (!/^[a-zA-Z0-9_-]+$/.test(segment)) {
      return false;
    }

    // Exclude segments with multiple hyphens (likely URL slugs like "parolis-ag-dgenis-dadastureba")
    // Tokens rarely have multiple hyphens
    const hyphenCount = (segment.match(/-/g) || []).length;
    if (hyphenCount >= 2) {
      return false;
    }

    // Check for mixed case (common in tokens like Base64)
    const hasUpperCase = /[A-Z]/.test(segment);
    const hasLowerCase = /[a-z]/.test(segment);
    const hasDigits = /[0-9]/.test(segment);

    // Tokens from your PHP generator have mixed case AND digits
    // Must have at least 2 of these 3 characteristics
    const characterTypeCount = [hasUpperCase, hasLowerCase, hasDigits].filter(Boolean).length;

    if (characterTypeCount < 2) {
      return false; // Not enough character variety for a token
    }

    // Calculate character diversity (entropy indicator)
    const uniqueChars = new Set(segment.toLowerCase()).size;
    const diversityRatio = uniqueChars / segment.length;

    // High diversity suggests random token (>0.5 means very random)
    // Your PHP tokens will have high diversity
    if (diversityRatio > 0.5) {
      return true;
    }

    // For slightly lower diversity, require longer length
    if (diversityRatio > 0.4 && segment.length >= 25) {
      return true;
    }

    // Very long alphanumeric strings with mixed case+digits are likely tokens
    if (segment.length >= 30 && characterTypeCount >= 3) {
      return true;
    }

    return false;
  }

  /**
   * Sanitize URL by intelligently detecting and removing tokens
   * This prevents sensitive data (password reset tokens, email confirmation tokens, etc.)
   * from being sent to third-party analytics services
   *
   * @param url The URL to sanitize
   * @returns Sanitized URL with tokens replaced by :token placeholder
   *
   * @example
   * sanitizeUrl('https://example.com/reset-password/abc123XYZ456def789')
   * // Returns: 'https://example.com/reset-password/:token'
   */
  sanitizeUrl(url: string): string {
    if (!url) {
      return url;
    }

    try {
      const urlObj = new URL(url);
      const pathSegments = urlObj.pathname.split('/').filter(Boolean);

      // Scan all path segments for tokens (not just the last one)
      let sanitized = false;
      for (let i = 0; i < pathSegments.length; i++) {
        if (this.isLikelyToken(pathSegments[i])) {
          pathSegments[i] = ':token';
          sanitized = true;
        }
      }

      if (sanitized) {
        urlObj.pathname = '/' + pathSegments.join('/');
      }

      // Redact sensitive query parameters
      const sensitiveParams = ['token', 'hash', 'code', 'key', 'secret', 'password', 'email', 'reset'];
      sensitiveParams.forEach(param => {
        if (urlObj.searchParams.has(param)) {
          urlObj.searchParams.set(param, ':redacted');
          sanitized = true;
        }
      });

      return urlObj.toString();
    } catch (error) {
      // If URL parsing fails, return original URL
      console.warn('Failed to sanitize URL:', error);
      return url;
    }
  }

  /**
   * Push an event to the dataLayer
   * @param event The event object to push
   */
  private pushToDataLayer(event: DataLayerObject): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    try {
      this.dataLayer.push(event);
      this.logEvent(event);
    } catch (error) {
      console.error('Error pushing to dataLayer:', error);
    }
  }

  /**
   * Check if GTM is loaded
   */
  isGTMLoaded(): boolean {
    return isPlatformBrowser(this.platformId) && !!this.window?.google_tag_manager;
  }

  /**
   * Log event to console (development only)
   */
  private logEvent(event: DataLayerObject): void {
    if (typeof console !== 'undefined' && console.log) {
      console.log(`[GTM] ${event.event}:`, event);
    }
  }

  // ===== Event Methods =====

  /**
   * Push a page view event
   */
  pushPageView(data: Omit<PageViewEvent, 'event'>): void {
    this.pushToDataLayer({
      event: 'pageView',
      ...data,
    } as PageViewEvent);
  }

  /**
   * Push a generic event
   */
  pushGenericEvent(data: Omit<GenericEvent, 'event'>): void {
    this.pushToDataLayer({
      event: 'genericEvent',
      ...data,
    } as GenericEvent);
  }

  /**
   * Push a promo impressions event
   */
  pushPromoImpressions(data: Omit<PromoImpressionsEvent, 'event'>): void {
    this.pushToDataLayer({
      event: 'EEC_promoImpressions',
      ...data,
    } as PromoImpressionsEvent);
  }

  /**
   * Push a promo click event
   */
  pushPromoClick(data: Omit<PromoClickEvent, 'event'>): void {
    this.pushToDataLayer({
      event: 'EEC_promoClick',
      ...data,
    } as PromoClickEvent);
  }

  /**
   * Push a product impressions event
   */
  pushProductImpressions(data: Omit<ProductImpressionsEvent, 'event'>): void {
    this.pushToDataLayer({
      event: 'EEC_productImpressions',
      ...data,
    } as ProductImpressionsEvent);
  }

  /**
   * Push a product click event
   */
  pushProductClick(data: Omit<ProductClickEvent, 'event'>): void {
    this.pushToDataLayer({
      event: 'EEC_productClick',
      ...data,
    } as ProductClickEvent);
  }

  /**
   * Push a product detail event
   */
  pushProductDetail(data: Omit<ProductDetailEvent, 'event'>): void {
    this.pushToDataLayer({
      event: 'EEC_productDetail',
      ...data,
    } as ProductDetailEvent);
  }

  /**
   * Push an add to cart event
   */
  pushAddToCart(data: Omit<AddToCartEvent, 'event'>): void {
    this.pushToDataLayer({
      event: 'EEC_addToCart',
      ...data,
    } as AddToCartEvent);
  }

  /**
   * Push a remove from cart event
   */
  pushRemoveFromCart(data: Omit<RemoveFromCartEvent, 'event'>): void {
    this.pushToDataLayer({
      event: 'EEC_removeFromCart',
      ...data,
    } as RemoveFromCartEvent);
  }

  /**
   * Push a checkout event
   */
  pushCheckout(data: Omit<CheckoutEvent, 'event'>): void {
    this.pushToDataLayer({
      event: 'EEC_checkout',
      ...data,
    } as CheckoutEvent);
  }

  /**
   * Push a checkout option event
   */
  pushCheckoutOption(data: Omit<CheckoutOptionEvent, 'event'>): void {
    this.pushToDataLayer({
      event: 'EEC_checkoutOption',
      ...data,
    } as CheckoutOptionEvent);
  }

  /**
   * Push a purchase event
   */
  pushPurchase(data: Omit<PurchaseEvent, 'event'>): void {
    this.pushToDataLayer({
      event: 'EEC_orderConfirmation',
      ...data,
    } as PurchaseEvent);
  }

  /**
   * Clear ecommerce object before pushing new ecommerce event
   * This is a GTM best practice
   */
  clearEcommerce(): void {
    this.pushToDataLayer({
      event: 'clearEcommerce',
      ecommerce: null,
    } as any);
  }
}
